import apiRequests from 'utils/api';
import apiRoutes from 'config/apiRoute';
import type { TimeEntry } from 'services/timeTracking';

export interface UserPerformanceCategories {
  billable: number;
  'non-billable': number;
}

export interface UserPerformanceEntry {
  user: { uuid: string; name: string };
  categories: UserPerformanceCategories;
}

export interface UserPerformanceReport {
  general: UserPerformanceCategories;
  per_user: UserPerformanceEntry[];
}

export interface FetchUserPerformanceParams {
  dateFrom?: string;
  dateTo?: string;
  userTypes?: string[];
  source?: string[];
  projectPlan?: string[];
  categories?: string[];
  userUuid?: string;
  abort?: AbortController;
}

const PAGE_SIZE = 500;
const MAX_PAGES = 60;

const emptyCategories = (): UserPerformanceCategories => ({ billable: 0, 'non-billable': 0 });

const buildParams = (filters: FetchUserPerformanceParams): Record<string, any> => {
  const params: Record<string, any> = {};

  if (filters.dateFrom && filters.dateTo) {
    params['filter[between_dates]'] = `${filters.dateFrom},${filters.dateTo}`;
  }

  if (filters.userTypes?.length) {
    params['filter[user_type]'] = filters.userTypes.join(',');
  }

  if (filters.source?.length) {
    params['filter[source]'] = filters.source.join(',');
  }

  if (filters.projectPlan?.length) {
    params['filter[plan]'] = filters.projectPlan.join(',');
  }

  if (filters.userUuid) {
    params['filter[user]'] = filters.userUuid;
  }

  const hasBillable = !!filters.categories?.includes('billable');
  const hasNonBillable = !!filters.categories?.includes('non-billable');

  if (hasBillable && !hasNonBillable) {
    params['filter[billable]'] = true;
  } else if (!hasBillable && hasNonBillable) {
    params['filter[billable]'] = false;
  }

  return params;
};

const aggregate = (entries: TimeEntry[]): UserPerformanceReport => {
  const general = emptyCategories();
  const perUser = new Map<string, UserPerformanceEntry>();

  entries.forEach((entry) => {
    const bucket: keyof UserPerformanceCategories = entry.billable === false ? 'non-billable' : 'billable';
    const minutes = Number(entry.minutes) || 0;

    general[bucket] += minutes;

    const uuid = entry.user_uuid ?? 'unknown';

    let userEntry = perUser.get(uuid);

    if (!userEntry) {
      userEntry = { user: { uuid, name: entry.user_name ?? '—' }, categories: emptyCategories() };
      perUser.set(uuid, userEntry);
    }

    userEntry.categories[bucket] += minutes;
  });

  const totalMinutes = (entry: UserPerformanceEntry) => entry.categories.billable + entry.categories['non-billable'];

  const perUserList = Array.from(perUser.values()).sort((a, b) => totalMinutes(b) - totalMinutes(a));

  return { general, per_user: perUserList };
};

export const fetchUserPerformanceReport = async (filters: FetchUserPerformanceParams): Promise<UserPerformanceReport> => {
  const baseParams = buildParams(filters);
  const entries: TimeEntry[] = [];

  let page = 1;
  let keepGoing = true;

  while (keepGoing && page <= MAX_PAGES) {
    // eslint-disable-next-line no-await-in-loop
    const res = await apiRequests.get(
      apiRoutes.TIME_ENTRIES,
      { ...baseParams, 'page[number]': page, 'page[size]': PAGE_SIZE },
      {},
      filters.abort,
    );

    const rows: TimeEntry[] = res.data?.data ?? [];
    entries.push(...rows);

    const total = res.data?.meta?.total;
    keepGoing = typeof total === 'number' ? entries.length < total && rows.length > 0 : rows.length === PAGE_SIZE;

    page += 1;
  }

  return aggregate(entries);
};

export default fetchUserPerformanceReport;
